import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import BackToTop from '../components/layout/BackToTop';
import EnterpriseContactForm from '../components/forms/EnterpriseContactForm';
import enterpriseData from '../content/enterprise/index.json';
import { usePageMeta } from '../lib/usePageMeta';
import { Button } from '../components/ui/button';
import {
  Network, ArrowRight, Check, ShieldAlert, Palette, Rocket,
} from 'lucide-react';

const scrollToForm = () => {
  const el = document.querySelector('#enterprise-form');
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

const EnterpriseProvidersPage = () => {
  usePageMeta(
    'ResiAX Enterprise & Integration — For Complex Housing Operations',
    'Explore ResiAX as an interoperable resident-information, communication and evidence layer around your existing housing, property, repairs or CRM systems. Integration, governance, white-label and procurement pathways.'
  );

  React.useEffect(() => {
    if (window.location.hash) {
      setTimeout(() => {
        const el = document.querySelector(window.location.hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 200);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  const d = enterpriseData;

  return (
    <div className="min-h-screen bg-[#FFFAF8]">
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-20 bg-[#1F1E1E] text-white relative overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#B5541B]/10 rounded-full blur-3xl" />
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-6">
              <Network className="w-5 h-5 text-[#D4722F]" />
              <span className="text-sm font-semibold text-[#D4722F] uppercase tracking-wide">{d.hero.eyebrow}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6" data-testid="enterprise-hero-headline">
              {d.hero.headline}
            </h1>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-10">
              {d.hero.body}
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button
                onClick={scrollToForm}
                data-testid="enterprise-hero-discuss-btn"
                className="bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold h-auto px-8 py-6 text-lg rounded-full shadow-lg group"
              >
                {d.hero.primaryCta}
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-2 border-white/30 text-white hover:bg-white/10 font-semibold h-auto px-8 py-6 text-lg rounded-full"
              >
                <Link to="/providers" data-testid="enterprise-hero-pilot-link">{d.hero.secondaryCta}</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Fit */}
        <section className="py-20 bg-[#FFFAF8]" data-testid="enterprise-fit-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10 max-w-3xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1F1E1E] mb-4">{d.fit.heading}</h2>
              <p className="text-[#3B3535]/70 leading-relaxed">{d.fit.body}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {(d.fit.signals || []).map((s, i) => (
                <div key={i} className="flex items-start gap-2.5 bg-white rounded-xl p-4 border border-gray-100 shadow-sm text-sm text-[#3B3535]">
                  <Check className="w-4 h-4 text-[#B5541B] mt-0.5 shrink-0" />
                  {s}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Capability */}
        <section className="py-20 bg-white" data-testid="enterprise-capability-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10 max-w-3xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1F1E1E] mb-4">{d.capability.heading}</h2>
              <p className="text-[#3B3535]/70 leading-relaxed">{d.capability.intro}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              {(d.capability.examples || []).map((c, i) => (
                <div key={i} className="flex items-start gap-2.5 bg-[#FFF8F5] rounded-xl p-4 border border-[#B5541B]/10 text-sm text-[#3B3535]">
                  <Check className="w-4 h-4 text-[#B5541B] mt-0.5 shrink-0" />
                  {c}
                </div>
              ))}
            </div>
            <div className="flex items-start gap-3 bg-[#FFFAF8] border border-[#B5541B]/20 rounded-xl p-5 max-w-3xl mx-auto" data-testid="enterprise-integration-guardrail">
              <ShieldAlert className="w-5 h-5 text-[#B5541B] shrink-0 mt-0.5" />
              <p className="text-sm text-[#3B3535]/80 leading-relaxed font-medium">{d.capability.guardrail}</p>
            </div>
          </div>
        </section>

        {/* Discovery */}
        <section className="py-20 bg-[#1F1E1E] text-white" data-testid="enterprise-discovery-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">{d.discovery.heading}</h2>
            <div className="space-y-4">
              {(d.discovery.steps || []).map((step, i) => (
                <div key={i} className="flex items-start gap-4 bg-white/5 rounded-xl p-5 border border-white/10">
                  <span className="w-8 h-8 rounded-full bg-gradient-to-br from-[#B5541B] to-[#8B3F14] flex items-center justify-center text-sm font-bold shrink-0">
                    {i + 1}
                  </span>
                  <p className="text-white/85 pt-1">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* White-label */}
        <section className="py-16 bg-[#FFFAF8]" data-testid="enterprise-whitelabel-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm flex flex-col sm:flex-row items-start gap-5">
              <div className="w-12 h-12 rounded-xl bg-[#B5541B]/10 flex items-center justify-center shrink-0">
                <Palette className="w-6 h-6 text-[#B5541B]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#1F1E1E] mb-2">{d.whiteLabel.heading}</h3>
                <p className="text-sm text-[#3B3535]/75 leading-relaxed">{d.whiteLabel.body}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Enterprise form */}
        <section id="enterprise-form" className="py-20 bg-white scroll-mt-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1F1E1E] mb-3">{d.cta.heading}</h2>
              <p className="text-[#3B3535]/70">{d.cta.body}</p>
            </div>
            <EnterpriseContactForm />
          </div>
        </section>

        {/* Back to standalone pilot */}
        <section className="py-16 bg-[#FFFAF8]" data-testid="enterprise-pilot-backlink-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-[#1F1E1E] text-white rounded-2xl p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#D4722F] mb-3">
                  <Rocket className="w-4 h-4" />
                  Provider pilot
                </div>
                <h3 className="text-xl font-bold mb-2">{d.pilotLink.heading}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{d.pilotLink.body}</p>
              </div>
              <Link
                to="/providers"
                data-testid="enterprise-back-to-pilot-link"
                className="shrink-0 inline-flex items-center gap-2 bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold px-6 py-3 rounded-full transition-all group shadow-lg"
              >
                {d.pilotLink.ctaLabel}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default EnterpriseProvidersPage;
